import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

export const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { key: 'home', section: 'hero' },
    { key: 'audits', section: 'audits' },
    { key: 'services', section: 'services' },
    { key: 'about', section: 'about' },
    { key: 'testimonials', section: 'testimonials' },
    { key: 'contact', section: 'contact' }
  ];

  return (
    <>
      <nav 
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#004d40] shadow-lg py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-2 group"
              data-testid="logo-button"
            >
              <span className="font-serif text-2xl md:text-3xl font-bold text-white tracking-tight">
                BICENTER
              </span>
              <span className="text-[#4caf50] font-semibold text-lg md:text-xl">
                CONSULTING
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.section)}
                  className="nav-link text-white/90 hover:text-white font-medium text-sm uppercase tracking-wide"
                  data-testid={`nav-${item.key}`}
                >
                  {t(`nav.${item.key}`)}
                </button>
              ))}
            </div>

            {/* Right side: Language + CTA */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="lang-toggle text-white text-sm font-medium"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}</span>
              </button>

              {/* CTA Button */}
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-primary text-sm"
                data-testid="nav-book-call"
              >
                {t('nav.bookCall')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="lang-toggle text-white text-sm font-medium py-2 px-3"
                data-testid="mobile-language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span>{language.toUpperCase()}</span>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="text-white p-2"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`overlay ${isMobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        data-testid="mobile-menu"
      >
        <div className="flex justify-end mb-8">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-white p-2"
            data-testid="close-mobile-menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-col gap-6">
          {navItems.map((item) => (
            <button
              key={item.key}
              onClick={() => scrollToSection(item.section)}
              className="text-white text-lg font-medium text-left hover:text-[#4caf50] transition-colors duration-200"
              data-testid={`mobile-nav-${item.key}`}
            >
              {t(`nav.${item.key}`)}
            </button>
          ))}

          <button
            onClick={() => scrollToSection('contact')}
            className="btn-primary text-center mt-4"
            data-testid="mobile-book-call"
          >
            {t('nav.bookCall')}
          </button>
        </div>
      </div>
    </>
  );
};
